import { api } from "@lumberset/api-client";
import { MessageWorkspaceButton, supportQueries } from "@lumberset/support-ui";
import { ago, ReadAgain, ReviewMode, ReviewStatus } from "@lumberset/takeoff-ui";
import {
  Badge,
  Button,
  Container,
  EmptyState,
  ProgressBar,
  SectionHeader,
  Skeleton,
  Stat,
  Table,
  Tabs,
  Tooltip,
} from "@lumberset/ui";
import { ListCheckbox } from "@medusajs/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { type ReviewFilter, readAgainRun, reviewsQuery, stoppedRunsQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/")({ component: ReviewQueue });

/** What the queue says when a tab has nothing in it. */
const emptyCopy: Record<ReviewFilter, { title: string; description: string }> = {
  open: {
    title: "Nothing waiting",
    description: "Every Takeoff has been reviewed. New ones appear here as Reading finishes.",
  },
  prepared: {
    title: "Nothing prepared",
    description:
      "Takeoffs read through before their Reviewer, with a prep report at the top of the review, appear here.",
  },
  approved: { title: "Nothing approved yet", description: "Approved Reviews will be listed here." },
};

/** How far an open Review has got: lines checked, and what still stops approval. */
function QueueProgress({
  progress,
}: {
  progress: { linesChecked: number; lines: number; flagsOpen: number; blockers: number };
}) {
  const share = progress.lines === 0 ? 100 : (progress.linesChecked / progress.lines) * 100;
  return (
    <div className="flex min-w-[10rem] flex-col gap-y-1">
      <span className="txt-compact-small text-ui-fg-subtle">
        <span className="tabular text-ui-fg-base">
          {progress.linesChecked} of {progress.lines}
        </span>{" "}
        lines checked
        {progress.blockers > 0 ? (
          <span className="text-ui-tag-red-text">, {progress.blockers} blocking</span>
        ) : progress.flagsOpen > 0 ? (
          <span>, {progress.flagsOpen} flags open</span>
        ) : null}
      </span>
      <ProgressBar value={share} />
    </div>
  );
}

function ReviewQueue() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ReviewFilter>("open");
  const { data, isPending } = useQuery({ ...reviewsQuery(filter), refetchInterval: 15_000 });
  const { data: waiting } = useQuery({ ...reviewsQuery("open"), refetchInterval: 15_000 });
  const { data: support } = useQuery({ ...supportQueries.stats(api), refetchInterval: 60_000 });
  // Prepared reviews are open ones too, so they show what an open review shows.
  const open = filter !== "approved";

  return (
    <>
      <Crumbs trail={[{ label: "Review queue" }]} />

      {/* Today at a glance: what is waiting on Staff, before the queue itself. */}
      <Container className="grid grid-cols-2 gap-px overflow-hidden bg-ui-border-base p-0 lg:grid-cols-4">
        <Stat
          className="bg-ui-bg-base"
          label="Takeoffs to review"
          value={waiting?.reviews.length ?? "-"}
          hint="Held for a Reviewer"
        />
        <Stat
          className="bg-ui-bg-base"
          label="Open tickets"
          value={support?.open ?? "-"}
          hint={
            <Link to="/support" className="text-ui-fg-interactive hover:underline">
              Open support
            </Link>
          }
        />
        <Stat
          className="bg-ui-bg-base"
          label="Nobody on it"
          value={support?.unassigned ?? "-"}
          hint="Tickets without an assignee"
        />
        <div className="flex flex-col justify-center gap-y-2 bg-ui-bg-base px-6 py-4">
          <MessageWorkspaceButton
            client={api}
            onSent={(id) => navigate({ to: "/support/tickets/$id", params: { id } })}
          />
          <Button asChild size="small" variant="transparent" className="justify-start">
            <Link to="/help/articles/new">Write a help article</Link>
          </Button>
        </div>
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Review queue"
          description="Takeoffs waiting for a Reviewer. Held ones come first, oldest at the top."
        />

        <div className="px-6 py-3">
          <Tabs value={filter} onValueChange={(value) => setFilter(value as ReviewFilter)}>
            <Tabs.List>
              <Tabs.Trigger value="open">Open</Tabs.Trigger>
              <Tabs.Trigger value="prepared">Prepared</Tabs.Trigger>
              <Tabs.Trigger value="approved">Approved</Tabs.Trigger>
            </Tabs.List>
          </Tabs>
        </div>

        {isPending ? (
          <div className="flex flex-col gap-y-3 px-6 py-4">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-5 w-3/5" />
          </div>
        ) : !data || data.reviews.length === 0 ? (
          <EmptyState
            icon={<ListCheckbox />}
            title={emptyCopy[filter].title}
            description={emptyCopy[filter].description}
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Workspace</Table.HeaderCell>
                <Table.HeaderCell>Project</Table.HeaderCell>
                <Table.HeaderCell>Mode</Table.HeaderCell>
                <Table.HeaderCell className="max-md:hidden">Status</Table.HeaderCell>
                <Table.HeaderCell>{open ? "Waiting since" : "Approved"}</Table.HeaderCell>
                <Table.HeaderCell className="max-lg:hidden">
                  {open ? "Progress" : "Time spent"}
                </Table.HeaderCell>
                <Table.HeaderCell className="max-sm:hidden">Assignee</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.reviews.map((review) => (
                <Table.Row
                  key={review.id}
                  data-clickable="true"
                  onClick={() => navigate({ to: "/reviews/$id", params: { id: review.id } })}
                >
                  <Table.Cell className="txt-compact-small-plus whitespace-nowrap text-ui-fg-base">
                    {review.yardName}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {review.projectName}
                    {/* A newer takeoff of the project: this one can be closed if it replaces it. */}
                    {review.newerRelease ? (
                      <span className="txt-compact-xsmall block text-ui-tag-orange-text">
                        Newer takeoff released
                      </span>
                    ) : null}
                    {/* Read through before the Reviewer, from the operator command line. */}
                    {review.preparedAt && review.status !== "approved" ? (
                      <Tooltip
                        content={`Prepared for final review by ${review.preparedBy ?? "the operator"}, ${ago(review.preparedAt)}`}
                      >
                        <Badge color="purple" className="mt-1 flex">
                          Prepared
                        </Badge>
                      </Tooltip>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <ReviewMode mode={review.mode} status={review.status} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-md:hidden">
                    <ReviewStatus status={review.status} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {open
                      ? ago(review.createdAt)
                      : review.completedAt
                        ? ago(review.completedAt)
                        : "-"}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-lg:hidden">
                    {open ? (
                      review.progress ? (
                        <QueueProgress progress={review.progress} />
                      ) : (
                        <span className="text-ui-fg-muted">-</span>
                      )
                    ) : (
                      `${Math.max(1, Math.round(review.secondsSpent / 60))} min`
                    )}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-sm:hidden">
                    {review.assigneeName ?? <span className="text-ui-fg-muted">Unassigned</span>}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <StoppedTakeoffs />
    </>
  );
}

/**
 * Takeoffs whose reading failed, or stopped at the limit for one takeoff, that
 * can be sent back to be read again. Shown only when there are some. A stopped
 * one also waits in the queue above, to be completed by hand.
 */
function StoppedTakeoffs() {
  const queryClient = useQueryClient();
  const { data } = useQuery({ ...stoppedRunsQuery, refetchInterval: 30_000 });
  if (!data || data.runs.length === 0) return null;
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["takeoffs"] }),
      queryClient.invalidateQueries({ queryKey: ["reviews"] }),
    ]);

  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Stopped or failed takeoffs"
        description="Reading failed, or stopped at the limit for one takeoff, in the last 30 days. Send one back to be read again, or complete a stopped one by hand in its review."
      />
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Workspace</Table.HeaderCell>
            <Table.HeaderCell>Project</Table.HeaderCell>
            <Table.HeaderCell>What happened</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.runs.map((run) => (
            <Table.Row key={run.id}>
              <Table.Cell className="txt-compact-small-plus whitespace-nowrap text-ui-fg-base">
                {run.yardName}
              </Table.Cell>
              <Table.Cell className="whitespace-nowrap py-2.5">
                {run.projectName}
                <span className="txt-compact-xsmall block text-ui-fg-muted">
                  {run.planSetLabel}
                </span>
              </Table.Cell>
              <Table.Cell className="min-w-[200px] py-2.5">
                {run.from === "stopped" ? (
                  <span className="text-ui-tag-orange-text">Reading stopped at the limit</span>
                ) : (
                  <span className="text-pretty text-ui-fg-error">
                    {run.error ?? "Reading failed."}
                  </span>
                )}
                <span className="txt-compact-xsmall block text-ui-fg-muted">
                  {ago(run.finishedAt ?? run.createdAt)}
                  {run.from === "stopped" && run.reviewId ? (
                    <>
                      {", "}
                      <Link
                        to="/reviews/$id"
                        params={{ id: run.reviewId }}
                        className="text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
                      >
                        open its review
                      </Link>
                    </>
                  ) : null}
                </span>
              </Table.Cell>
              <Table.Cell className="w-[1%] py-2.5 text-right">
                {run.readAgain ? (
                  <ReadAgain
                    offer={run.readAgain}
                    projectName={run.projectName}
                    send={() => readAgainRun(run.id)}
                    onSent={refresh}
                  />
                ) : null}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Container>
  );
}
